export const services = [
  {
    id: "interior-design-proposals",
    title: "Interior Design Proposals",
    slug: "/services",
    description: "Complete, customized design plans tailored to your home, style, and budget — delivered in a clear, actionable format.",
    longDescription: "Our flagship service delivers a comprehensive interior design proposal covering every detail of your space. From layout planning and furniture arrangement to finish selections and color strategy, you receive a complete, actionable blueprint that removes uncertainty and makes confident decision-making possible — whether you're renovating, refreshing, or starting from scratch.",
    features: [
      "Full room-by-room layout planning",
      "Furniture arrangement & space optimization",
      "Color palette & material direction",
      "Budget-aligned recommendations",
      "Contractor-ready documentation",
    ],
    image: "https://public.readdy.ai/ai/img_res/13bec4351436428d916b91bf3941b15d.jpg",
  },
  {
    id: "renovation-planning",
    title: "Renovation Planning & Layout Design",
    slug: "/services",
    description: "Strategic layout planning and space optimization that transforms your renovation vision into a structured, executable plan.",
    longDescription: "Renovation projects fail when there is no clear plan. Our renovation planning service delivers structured layout proposals, spatial flow analysis, and contractor-ready direction that eliminates costly mid-project changes and ensures your renovation runs on time and within budget.",
    features: [
      "Spatial flow & layout optimization",
      "Before & after layout comparisons",
      "Trade coordination guidance",
      "Phased renovation planning",
      "Cost-saving structural recommendations",
    ],
    image: "https://readdy.ai/api/search-image?query=elegant%20open%20concept%20kitchen%20living%20renovation%20completed%20beautiful%20neutral%20warm%20tones%20white%20cabinetry%20marble%20island%20natural%20wood%20accents%20bright%20natural%20light%20contemporary%20high%20end%20residential%20staging%20magazine%20editorial&width=800&height=900&seq=svc-reno-elite-2&orientation=portrait",
  },
  {
    id: "material-finish-selection",
    title: "Material & Finish Selection",
    slug: "/services",
    description: "Curated material palettes, finish selections, and product recommendations that align with your aesthetic and budget.",
    longDescription: "The wrong material choice is one of the most expensive renovation mistakes. Our material selection service delivers a fully curated, cohesive palette of finishes, surfaces, hardware, and textiles — each individually vetted for quality, budget alignment, and aesthetic harmony across your entire space.",
    features: [
      "Full material & finish palette",
      "Floor, wall, and surface selections",
      "Hardware & fixture direction",
      "Textile & soft furnishing curation",
      "Vendor & product sourcing guidance",
    ],
    image: "https://readdy.ai/api/search-image?query=luxury%20interior%20material%20samples%20flat%20lay%20marble%20stone%20travertine%20fabric%20swatches%20warm%20neutral%20palette%20premium%20finishes%20wood%20veneer%20elegant%20curated%20editorial%20photography%20professional%20design%20studio&width=800&height=900&seq=svc-materials-elite-3&orientation=portrait",
  },
  {
    id: "room-design-packages",
    title: "Room-by-Room Design Packages",
    slug: "/services",
    description: "Focused design planning for individual rooms — from master bedrooms to kitchens — with complete detail and direction.",
    longDescription: "Not every project requires a full-home plan. Our room packages deliver the same level of professional detail as our full proposals — focused entirely on one space. Perfect for tackling a specific room that's been on your list, or as an entry point before a larger renovation.",
    features: [
      "Single room focus with full detail",
      "Furniture plan & placement",
      "Lighting design direction",
      "Styling & decor guidance",
      "Addable to multi-room bundles",
    ],
    image: "https://readdy.ai/api/search-image?query=stunning%20luxury%20master%20bedroom%20interior%20design%20neutral%20beige%20warm%20white%20tones%20elegant%20linen%20bedding%20upholstered%20headboard%20nightstands%20warm%20lamp%20light%20cozy%20sophisticated%20premium%20residential%20magazine%20photography&width=800&height=900&seq=svc-room-elite-4&orientation=portrait",
  },
  {
    id: "virtual-consultations",
    title: "Virtual Design Consultations",
    slug: "/services",
    description: "Remote-friendly design sessions that deliver professional guidance and structured plans without requiring onsite visits.",
    longDescription: "Get the same professional design quality as an in-person consultation — from anywhere in the country. Our virtual consultations combine live video sessions with detailed written follow-up plans, making expert interior design guidance accessible to every homeowner regardless of location.",
    features: [
      "Live video design session",
      "Post-session written plan",
      "Space analysis from provided photos",
      "Style profile development",
      "Nationwide availability",
    ],
    image: "https://readdy.ai/api/search-image?query=elegant%20home%20office%20workspace%20interior%20design%20neutral%20light%20wood%20desk%20chair%20plant%20natural%20light%20linen%20curtains%20minimalist%20premium%20contemporary%20residential%20comfortable%20productive%20calm%20aesthetic%20editorial%20photography&width=800&height=900&seq=svc-virtual-elite-5&orientation=portrait",
  },
];

export const testimonials = [
  {
    id: 1,
    name: "Sarah M.",
    location: "Denver, CO",
    initials: "SM",
    project: "Full Home Renovation",
    featured: true,
    quote: "Made our renovation simple and stress-free. We finally had a clear direction and knew exactly what to do at every step. The proposal paid for itself ten times over in mistakes we avoided. I genuinely can't recommend this enough.",
  },
  {
    id: 2,
    name: "James & Laura R.",
    location: "Austin, TX",
    initials: "JL",
    project: "Kitchen & Living Room Redesign",
    featured: false,
    quote: "Saved us thousands in bad decisions. The material selections alone prevented three costly mistakes we almost made. Our kitchen is stunning now.",
  },
  {
    id: 3,
    name: "Patricia K.",
    location: "Nashville, TN",
    initials: "PK",
    project: "Master Suite & Guest Room",
    featured: false,
    quote: "Finally had clarity on our design. I had been stuck for months. One proposal later and I knew exactly what my home needed. The process was so smooth.",
  },
  {
    id: 4,
    name: "Michael T.",
    location: "Charlotte, NC",
    initials: "MT",
    project: "Open-Concept Kitchen Renovation",
    featured: false,
    quote: "The remote process was seamless. I was skeptical at first but the proposal was incredibly detailed. My contractor was genuinely impressed by how thorough it was.",
  },
  {
    id: 5,
    name: "Amanda L.",
    location: "Phoenix, AZ",
    initials: "AL",
    project: "Living Room & Dining Area",
    featured: false,
    quote: "Clear, actionable, and beautifully presented. The design proposal gave us confidence. Our space looks amazing and we stayed within budget.",
  },
  {
    id: 6,
    name: "David & Cheryl W.",
    location: "Portland, OR",
    initials: "DW",
    project: "Basement & Home Office",
    featured: false,
    quote: "Helped us avoid the overwhelm. We had too many ideas and no direction. This gave us focus and we completed our renovation in record time.",
  },
  {
    id: 7,
    name: "Rachel B.",
    location: "Atlanta, GA",
    initials: "RB",
    project: "Primary Bedroom Suite",
    featured: false,
    quote: "I was blown away by the level of detail. Every material, finish, and furniture piece was thoughtfully chosen. It felt like a luxury design firm at a fraction of the cost.",
  },
  {
    id: 8,
    name: "Tom & Elise H.",
    location: "Seattle, WA",
    initials: "TH",
    project: "Full Home Redesign",
    featured: false,
    quote: "Worth every dollar. The proposal gave our contractor a clear roadmap. We finished on time, under budget, and the result is better than we imagined.",
  },
];

export const faqItems = [
  {
    question: "Who provides interior design services near me?",
    answer: "The Design Proposal offers professional interior design planning both virtually and locally. We serve homeowners across all 50 states with complete design proposals covering layout plans, material selections, and renovation guidance. Local in-person services are available in select metropolitan areas. Contact us to check availability in your location.",
  },
  {
    question: "How much does an interior design proposal cost?",
    answer: "Interior design proposal pricing varies based on project scope, room count, and detail level. Most clients invest between a few hundred to a few thousand dollars for a complete plan. This investment typically saves significantly more by preventing costly renovation mistakes, improving material choices, and streamlining the entire design process.",
  },
  {
    question: "How long does a design proposal take to complete?",
    answer: "Most interior design proposals are completed within 5 to 14 business days depending on project complexity and scope. Single-room packages can be delivered more quickly, while full-home renovation plans require additional time for thorough detail. Timelines begin once all project information and preferences are confirmed.",
  },
  {
    question: "Can interior design services be done remotely?",
    answer: "Yes, our entire process is designed to work seamlessly online. Clients across the country receive complete, detailed design proposals without requiring in-person meetings. Professional interior design planning is fully accessible regardless of location through our proven remote-first workflow.",
  },
  {
    question: "What is included in a design proposal?",
    answer: "A complete design proposal includes layout planning, finish and material selections, furniture direction, style cohesion strategy, and budget-aligned recommendations — all delivered in a clear, actionable format ready to hand directly to contractors or execute independently.",
  },
];

export const processSteps = [
  {
    number: "01",
    title: "Discovery Consultation",
    description: "We start by understanding your goals, style preferences, timeline, and budget. This foundation shapes every decision in your design plan and ensures nothing is assumed.",
  },
  {
    number: "02",
    title: "Design Development",
    description: "We develop layouts, curate material selections, establish a clear design direction, and review all options — tailored entirely to your unique space and goals.",
  },
  {
    number: "03",
    title: "Proposal Delivery",
    description: "You receive a structured, actionable design proposal — clearly documented, visually presented, and ready to execute with confidence and without guesswork.",
  },
  {
    number: "04",
    title: "Execution Support",
    description: "We provide guidance throughout the implementation phase, ensuring your renovation or redesign stays on track, on budget, and true to the original vision.",
  },
];

export const whyChooseUs = [
  { icon: "ri-file-list-3-line", text: "Structured proposals, not vague mood boards" },
  { icon: "ri-money-dollar-circle-line", text: "Budget-aligned recommendations that save money" },
  { icon: "ri-global-line", text: "Remote-friendly — professional service from anywhere" },
  { icon: "ri-time-line", text: "Faster decisions, less overwhelm throughout the project" },
  { icon: "ri-shield-check-line", text: "Prevents costly mistakes before they happen" },
  { icon: "ri-user-heart-line", text: "Client-first thinking in every single recommendation" },
  { icon: "ri-scissors-cut-line", text: "No unnecessary complexity or overselling" },
  { icon: "ri-award-line", text: "500+ successful proposals delivered nationwide" },
];

export const stats = [
  { number: "500+", label: "Design Proposals Delivered" },
  { number: "98%", label: "Client Satisfaction Rate" },
  { number: "12+", label: "Years of Design Experience" },
  { number: "50", label: "States Served Nationwide" },
];
